import React from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  MUTATE_CLEAR_TPMS_EVENT_CACHE,
  MUTATE_TRACK_LENGTH,
  QUERY_STINTS_WITH_TPMS_FOR_ALL_CARS,
  QUERY_TRACK_LENGTH,
  QUERY_UNIQUE_EVENTS,
} from "../../../../utils/graphql/queries/TPMSQueries";
import styles from "./style.module.scss";
import IdsSwitch from "../IdSwitch";

type EventHeaderProps = {
  selectedEvent: string;
  setSelectedEvent: (event: string) => void;
  trackLengthKm: number | "";
  setTrackLengthKm: (length: number | "") => void;
  cacheClearDisabled: boolean;
};

export default function EventHeader({
  selectedEvent,
  setSelectedEvent,
  trackLengthKm,
  setTrackLengthKm,
  cacheClearDisabled,
}: EventHeaderProps) {
  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
  } = useQuery(QUERY_UNIQUE_EVENTS);

  const events = React.useMemo(
    () => eventsData?.getFIAEvents ?? [],
    [eventsData],
  );

  const [selectedSeason, setSelectedSeason] = React.useState("");

  const seasons: string[] = React.useMemo(() => {
    const seasonSet = new Set<string>();
    for (const event of events) {
      const prefix = event.eventCode?.slice(0, 2);
      if (prefix) seasonSet.add(prefix);
    }
    return Array.from(seasonSet).sort((a, b) => b.localeCompare(a));
  }, [events]);

  const filteredEvents = React.useMemo(() => {
    if (!selectedSeason) return events;
    return events.filter((e: { eventCode: string; location: string }) =>
      e.eventCode?.startsWith(selectedSeason),
    );
  }, [events, selectedSeason]);

  const handleSeasonChange = (season: string) => {
    setSelectedSeason(season);
    setSelectedEvent("");
  };

  const [isSaving, setIsSaving] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState<null | boolean>(null);
  const [setTrackLengthMutation] = useMutation(MUTATE_TRACK_LENGTH);
  const client = useApolloClient();
  const [clearCacheMutation] = useMutation<
    { clearTPMSEventCache: { eventCode: string; deletedCount: number } },
    { eventCode: string }
  >(MUTATE_CLEAR_TPMS_EVENT_CACHE);
  const [cachePhase, setCachePhase] = React.useState<
    "clearing" | "rebuilding" | null
  >(null);
  const isUpdatingCache = cachePhase !== null;
  const [cacheClearFeedback, setCacheClearFeedback] = React.useState<{
    eventCode: string;
    message: string;
    error: boolean;
  } | null>(null);

  const clearEventCache = async () => {
    if (!selectedEvent || cacheClearDisabled || isUpdatingCache) return;
    const eventCode = selectedEvent;
    let cacheCleared = false;
    setCacheClearFeedback(null);
    setCachePhase("clearing");
    try {
      const { data } = await clearCacheMutation({
        variables: { eventCode },
        context: { skipRetry: true },
      });
      if (!data?.clearTPMSEventCache) {
        throw new Error(
          "The server did not confirm that the cache was cleared.",
        );
      }
      const { deletedCount } = data.clearTPMSEventCache;
      cacheCleared = true;
      setCachePhase("rebuilding");
      // Reuse the summary query's normal cache-building process after clearing.
      await client.query({
        query: QUERY_STINTS_WITH_TPMS_FOR_ALL_CARS,
        variables: { eventCode },
        fetchPolicy: "network-only",
        context: { skipRetry: true, queryDeduplication: false },
      });
      setCacheClearFeedback({
        eventCode,
        message: `Cleared ${deletedCount} cached record${
          deletedCount === 1 ? "" : "s"
        } for ${eventCode}`,
        error: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Please try again.";
      setCacheClearFeedback({
        eventCode,
        message: cacheCleared
          ? `Cleared cache for ${eventCode}, but could not rebuild it: ${message}`
          : `Could not clear cache for ${eventCode}: ${message}`,
        error: true,
      });
    } finally {
      setCachePhase(null);
    }
  };

  const saveTrackLength = async () => {
    if (!selectedEvent || trackLengthKm === "" || isNaN(Number(trackLengthKm)))
      return;
    setIsSaving(true);
    try {
      await setTrackLengthMutation({
        variables: {
          eventCode: selectedEvent,
          trackLengthKm: Number(trackLengthKm),
        },
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(null), 1200);
    } catch {
      setSaveSuccess(false);
      setTimeout(() => setSaveSuccess(null), 1200);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to display event label
  const getEventLabel = (event: { eventCode: string; location: string }) =>
    `${event.eventCode} - ${event.location}`;

  const { data: trackLengthData } = useQuery(QUERY_TRACK_LENGTH, {
    variables: { eventCode: selectedEvent },
    skip: !selectedEvent,
    fetchPolicy: "network-only",
  });

  React.useEffect(() => {
    if (
      selectedEvent &&
      trackLengthData &&
      typeof trackLengthData.getTrackLength === "number"
    ) {
      setTrackLengthKm(trackLengthData.getTrackLength);
    }
    if (
      selectedEvent &&
      trackLengthData &&
      (trackLengthData.getTrackLength === null ||
        trackLengthData.getTrackLength === undefined)
    ) {
      setTrackLengthKm("");
    }
  }, [selectedEvent, trackLengthData]);

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <select
          id="season"
          value={selectedSeason}
          disabled={isUpdatingCache}
          onChange={(e) => handleSeasonChange(e.target.value)}
        >
          <option value="">All Seasons</option>
          {seasons.map((season) => (
            <option key={season} value={season}>
              {season}
            </option>
          ))}
        </select>
        <select
          id="event"
          value={selectedEvent}
          disabled={isUpdatingCache}
          onChange={(e) => setSelectedEvent(e.target.value)}
        >
          <option value="" disabled>
            Select Event
          </option>
          {eventsLoading && <option>Loading...</option>}
          {eventsError && <option>Error loading events</option>}
          {!eventsLoading &&
            !eventsError &&
            filteredEvents
              .slice()
              .reverse()
              .map((event: { eventCode: string; location: string }) => (
                <option key={event.eventCode} value={event.eventCode}>
                  {getEventLabel(event)}
                </option>
              ))}
        </select>
        {/* Refresh Button */}
        <button
          type="button"
          title="Refresh Event"
          className={styles.refreshButton}
          disabled={isUpdatingCache}
          onClick={() => {
            setSelectedEvent("");
            setTimeout(() => {
              setSelectedEvent(selectedEvent);
            }, 0);
          }}
        >
          &#x21bb; Refresh
        </button>
        <div className={styles.idsSwitchWrapper}>
          <IdsSwitch />
        </div>
        <div className={styles.trackLengthImport}>
          <label htmlFor="trackLengthInput" style={{ marginRight: 8 }}>
            Track length (km):
          </label>
          <input
            id="trackLengthInput"
            type="number"
            min="0"
            step="0.01"
            value={trackLengthKm}
            onChange={(e) => {
              const val = e.target.value;
              setTrackLengthKm(val === "" ? "" : parseFloat(val));
            }}
            style={{
              width: 60,
              padding: "1px 4px",
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: "0.95em",
            }}
            placeholder="e.g. 2.3"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={saveTrackLength}
            disabled={!selectedEvent || isSaving || trackLengthKm === ""}
            className={
              saveSuccess === true
                ? styles.uploadButtonSuccess
                : saveSuccess === false
                ? styles.uploadButtonError
                : styles.uploadButton
            }
            style={{ marginLeft: 6 }}
          >
            {isSaving
              ? "Saving..."
              : saveSuccess === true
              ? "✓"
              : saveSuccess === false
              ? "!"
              : "Upload"}
          </button>
        </div>
        <div className={styles.clearCacheControl}>
          <button
            type="button"
            className={styles.clearCacheButton}
            onClick={clearEventCache}
            disabled={!selectedEvent || cacheClearDisabled || isUpdatingCache}
            title="Clear and rebuild cached data for all sessions in this event, preserving track length"
          >
            {cachePhase === "clearing"
              ? "Clearing cache..."
              : cachePhase === "rebuilding"
              ? "Rebuilding cache..."
              : "Clear Cache for this Event"}
          </button>
          {cacheClearFeedback?.eventCode === selectedEvent ? (
            <span
              className={styles.clearCacheFeedback}
              role={cacheClearFeedback.error ? "alert" : "status"}
            >
              {cacheClearFeedback.message}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
